// One button for finishing the Prepare & Send screen (mockup:
// docs/design/esign-prepare-and-send-toolbar-mockup-v1.html). The main press
// is the screen's real next step, Send. The caret holds the quieter ways to
// finish: sign now, keep it as a draft, update the template. Before this,
// those were four buttons stacked in the panel's footer.
//
// CONSUMER: internal-ui.
//
// DECISION: this component owns only the shape (a split button and its menu).
// Every label, every disabled rule and every handler is passed in by the
// screen, which already knows them. The disabled rules in particular are NOT
// restated here: a second copy of "when may this send" is how a button turns
// gray in one place and live in another.

import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

export interface FinishAction {
  key: string;
  label: string;
  /** One quiet line under the label in the menu. */
  hint?: string;
  title?: string;
  disabled?: boolean;
  onClick: () => void;
}

export interface FinishSplitButtonProps {
  primary: FinishAction;
  /** The menu. Empty means no caret at all. */
  more: readonly FinishAction[];
}

export function FinishSplitButton({ primary, more }: FinishSplitButtonProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Closes on a click anywhere else and on Escape, like every menu here.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="esign-finish" ref={rootRef} data-no-autosave>
      <button
        type="button"
        className="esign-finish-main"
        data-solo={more.length === 0 ? "true" : undefined}
        onClick={primary.onClick}
        disabled={primary.disabled}
        title={primary.title}
      >
        {primary.label}
      </button>
      {more.length > 0 ? (
        <>
          <button
            type="button"
            className="esign-finish-caret"
            aria-haspopup="menu"
            aria-expanded={open ? "true" : "false"}
            aria-label="More ways to finish"
            onClick={() => setOpen((v) => !v)}
          >
            <ChevronDown size={16} strokeWidth={2.5} aria-hidden="true" />
          </button>
          {open ? (
            <div className="esign-finish-menu" role="menu">
              {more.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  role="menuitem"
                  className="esign-finish-item"
                  disabled={a.disabled}
                  title={a.title}
                  onClick={() => {
                    setOpen(false);
                    a.onClick();
                  }}
                >
                  <span>{a.label}</span>
                  {a.hint ? <small>{a.hint}</small> : null}
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
